/**
 * Cube storage generator: the shared case generator in its `equal` layout mode (taxonomy §3).
 * One column of full-width shelves whose rows all carry the same divider list; with
 * `intersection: 'egg-crate'` the short dividers that line up are merged into one full-height
 * divider per line, crossing every shelf in an egg-crate halving.
 */
import type { BuildModel, Design, Issue } from '../../domain/schemas';
import type { Hardware } from '../assembly';
import { parseConstructionProfile, type ConstructionProfile } from '../../domain/profiles';
import { fmtMm } from '../../domain/units';
import { generateCase, type CaseBoard } from '../case';
import { assembleBuild, evenSplit, familyJoints, familyStock, fromCase, layable, minHeightFix, sizeErrors, THIN_STOCK_ID, type FamilyBoard, type FamilyJoint } from '../build-common';
import { resolveStock, STOCKS, type AnyStock } from '../stock';
import jointsJson from './joints.json' with { type: 'json' };
import { CUBE_ID, CUBE_LABEL, CUBE_VERSION, cubeParamsOf } from './design';
import { baseErrors, baseOf, feetHardware, feetSupports, footKindFor, liftOf, slatErrors, slatsOf } from '../base';

const parsed = parseConstructionProfile(jointsJson);
if (!parsed.ok) throw new Error(`cube-storage joints.json is invalid: ${parsed.errors.join('; ')}`);
export const CUBE_JOINTS: ConstructionProfile = Object.freeze(parsed.value) as ConstructionProfile;

export function generateCube(design: Design, stock?: AnyStock | null, construction?: ConstructionProfile): BuildModel {
  const c = familyJoints(CUBE_ID, construction, CUBE_JOINTS);
  const S = familyStock(design, stock, STOCKS, 'birch-ply-18');
  const R = resolveStock(S);
  const t = R.thicknessTicks;
  const p = cubeParamsOf(design);
  const { W, H, D } = design;
  const errors: Issue[] = sizeErrors(design, c);
  const warnings: Issue[] = [];
  const env = c.envelope.n;
  for (const k of ['cols', 'rows'] as const) if (!Number.isInteger(p[k]) || p[k] < env.min || p[k] > env.max) errors.push({ field: k, message: `${k === 'cols' ? 'Columns' : 'Rows'} must be between ${env.min} and ${env.max}.` });
  const foot = footKindFor(R.kind);
  const base = baseOf(p, foot);
  // FND-23: a thick solid frame (38 mm boards and up) has mitred corners and through-tenoned shelves
  const thick = R.kind === 'board' && t >= 380;
  const lift = liftOf(base);
  const slats = slatsOf(p);
  if (!(p.plinth >= 0 && p.plinth <= 1500)) errors.push({ field: 'plinth', message: 'The plinth can be at most 150 mm high.', fix: { label: 'Use a 150 mm plinth', patch: { plinth: 1500 } } });
  else if (p.base !== undefined) errors.push(...baseErrors(base, D, foot));
  errors.push(...slatErrors(slats, D));
  if (slats && p.back === 'rebated') errors.push({ field: 'back', message: 'Slatted sides cannot carry a rebated back; use an overlay back or none.', fix: { label: 'Use an overlay back', patch: { back: 'overlay' } } });
  const hardware: Hardware[] = [];
  let boards: FamilyBoard[] = [];
  let joints: FamilyJoint[] = [];
  let openings: { id: string; label: string; x0: number; x1: number; y0: number; y1: number }[] = [];
  let datums: { partId: string; label: string; ticks: number }[] = [];
  // the layout needs only whole counts and positive sizes: rules that fail never empty the model
  const shapeOk = layable(design) && [p.cols, p.rows].every((n) => Number.isInteger(n) && n >= 1 && n <= 20);
  const clearW = W - 2 * t - (p.cols - 1) * t;
  const clearH = H - lift - 2 * t - (p.rows - 1) * t;
  if (shapeOk) {
    // sizes that leave no room still lay out (every cell at least 1 mm, the case growing past H or W) so the parts show beside the error
    const cells = evenSplit(clearW, p.cols).map((g) => Math.max(g, 10));
    const rows = evenSplit(clearH, p.rows).map((g) => Math.max(g, 10));
    const minCell = c.envelope.minOpeningTicks;
    const thinT = resolveStock(STOCKS[THIN_STOCK_ID]!).thicknessTicks;
    if (Math.min(...evenSplit(clearW, p.cols)) < minCell) {
      const fix = minHeightFix({ count: p.cols, minCount: env.min, key: 'cols', noun: 'column', t, thinT, min: minCell, currentStock: S.id, clearWith: (n, tt) => Math.floor((W - 2 * tt - (n - 1) * tt) / n) });
      errors.push({ field: 'cols', message: `Cells would be ${fmtMm(Math.min(...evenSplit(clearW, p.cols)))} mm wide; the narrowest is ${fmtMm(minCell)} mm.`, ...(fix ? { fix } : {}) });
    }
    if (Math.min(...evenSplit(clearH, p.rows)) < minCell) {
      const fix = minHeightFix({ count: p.rows, minCount: env.min, key: 'rows', noun: 'row', t, thinT, min: minCell, currentStock: S.id, clearWith: (n, tt) => Math.floor((H - lift - 2 * tt - (n - 1) * tt) / n) });
      errors.push({ field: 'rows', message: `Cells would be ${fmtMm(Math.min(...evenSplit(clearH, p.rows)))} mm high; the lowest is ${fmtMm(minCell)} mm.`, ...(fix ? { fix } : {}) });
    }
    const b = p.back === 'none' ? 0 : R.back.thicknessTicks;
    const out = generateCase({
      W, H, D, t, plinth: p.plinth, base, slats, corner: thick ? 'mitre' : 'butt', back: { kind: p.back, thickness: b }, shelfJoint: thick ? 'through-tenon' : 'screwed-butt', rowDividerJoint: 'housed-dado',
      columns: [{ clear: W - 2 * t, rows: rows.map((g) => ({ clear: g, cells })) }],
      naming: {
        shelf: (_c, k) => ({ id: `shelf-${k}`, label: `Shelf ${k}` }),
        divider: (k) => ({ id: `divider-${k}`, label: `Divider ${k}` }),
        rowDivider: (_c, r, k) => ({ id: `rowdiv-${r + 1}-${k}`, label: `Row ${r + 1} divider ${k}` }),
        back: (k, n) => (n === 1 ? { id: 'back', label: 'Back' } : { id: `back-${k}`, label: `Back ${k} of ${n}` }),
        opening: (_c, r, cell) => ({ id: `cell-${r + 1}-${cell + 1}`, label: `Row ${r + 1}, cube ${cell + 1}` }),
      },
    });
    let caseBoards: CaseBoard[] = out.boards;
    let caseJoints = out.joints;
    if (p.intersection === 'egg-crate' && p.cols > 1) {
      // merge each line of short dividers into one full-height divider crossing every shelf
      const short = new Set(caseBoards.filter((q) => q.id.startsWith('rowdiv-')).map((q) => q.id));
      caseBoards = caseBoards.filter((q) => !short.has(q.id));
      caseJoints = caseJoints.filter((j) => !short.has(j.a) && !short.has(j.b));
      const y0 = lift + t, y1 = H - t;
      const z0 = out.interior.z0;
      const shelves = caseBoards.filter((q) => q.role === 'shelf');
      let x = t;
      const at = caseBoards.findIndex((q) => q.id === 'top');
      const merged: CaseBoard[] = [];
      for (let k = 1; k < p.cols; k++) {
        x += cells[k - 1]!;
        const id = `divider-${k}`;
        merged.push({ id, role: 'divider', label: `Divider ${k}`, l: y1 - y0, w: D - z0, t, grainWorld: 'y', box: { x, y: y0, z: z0, sx: t, sy: y1 - y0, sz: D - z0 }, stock: 'carcass', machining: shelves.length ? `${shelves.length} egg-crate halving${shelves.length === 1 ? '' : 's'}, notched from the front to half depth` : undefined });
        caseJoints.push({ a: id, b: 'bottom', kind: 'screwed-butt', face: 'top', datum: y0 / 10 });
        caseJoints.push({ a: id, b: 'top', kind: 'screwed-butt', face: 'underside', datum: y1 / 10 });
        for (const s of shelves) caseJoints.push({ a: s.id, b: id, kind: 'egg-crate', face: 'crossing', datum: (s.datum ?? 0) / 10 });
        x += t;
      }
      for (const s of shelves) if (p.cols > 1) s.machining = `${p.cols - 1} egg-crate halving${p.cols - 1 === 1 ? '' : 's'}, notched from the back to half depth`;
      caseBoards.splice(at, 0, ...merged);
      // the back joins every merged divider it touches
      for (const bk of caseBoards.filter((q) => q.role === 'back')) for (const m of merged) caseJoints.push({ a: bk.id, b: m.id, kind: 'screwed-butt', face: 'back', datum: 0 });
    }
    const f = fromCase({ boards: caseBoards, joints: caseJoints });
    boards = f.boards; joints = f.joints;
    if (base.kind === 'feet') hardware.push(...feetHardware(lift, feetSupports(caseBoards), foot));
    if (p.back !== 'none') {
      const bk = boards.find((q) => q.role === 'back');
      const sheet = R.back.sheet;
      if (bk && !(Math.max(bk.l, bk.w) <= sheet.lengthTicks - 2 * c.edgeAllowanceTicks && Math.min(bk.l, bk.w) <= sheet.widthTicks - 2 * c.edgeAllowanceTicks)) {
        errors.push({ field: 'back', message: `The back (${fmtMm(bk.box.sx)} × ${fmtMm(bk.box.sy)} mm) is larger than one sheet; choose no back or a smaller case.` });
      }
    }
    openings = out.openings.map((o) => ({ id: o.id, label: o.label, x0: o.x0, x1: o.x1, y0: o.y0, y1: o.y1 }));
    datums = out.datums.map((d) => ({ partId: d.partId, label: d.label, ticks: d.ticks }));
    if (cells[0]! > Math.round(c.warnings.maxShelfSpanMm * 10)) {
      const more = p.cols + 1;
      const fits = more <= env.max && (W - 2 * t - (more - 1) * t) / more >= minCell;
      warnings.push({ field: 'cols', message: `Shelves span ${fmtMm(cells[0]!)} mm between dividers; under a full load of books they may sag.`, ...(fits ? { fix: { label: `Add a divider (${more} columns)`, patch: { cols: more } } } : {}) });
    }
  }
  if (H > Math.round(c.warnings.antiTipAboveMm * 10)) warnings.push({ field: 'H', message: `At ${fmtMm(H)} mm tall this must be fixed to the wall (anti-tip).` });
  return assembleBuild({
    familyId: CUBE_ID, familyVersion: CUBE_VERSION, familyLabel: CUBE_LABEL, design, stock: S, construction: c,
    boards, joints, openings, datums, errors, warnings, hardware, clearWidth: W - 2 * t, shelves: Math.max(0, p.rows - 1),
  });
}
